import {captureException} from '@sentry/browser';
import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil} from 'ramda';

import {i18n} from '@dms/i18n';

import {Nullish, openInNewTab} from 'shared/web';

import {ServiceCaseInvoiceType} from '../types/ServiceCaseInvoiceType';
import {downloadBlobFile} from '../utils/downloadBlobFile';
import {exportServiceCaseInvoicePdf} from '../utils/exportServiceCaseInvoicePdf';
import {printPDF} from '../utils/printPDF';

const PDF_URL_REVOKE_DELAY_MS = 60_000;

const openPdfInNewTab = (pdf: Blob, fileName: string) => {
  const url = URL.createObjectURL(pdf);
  const openedTab = openInNewTab(url);

  // A popup allowed minutes later would otherwise open an already revoked object URL.
  if (isNil(openedTab)) {
    URL.revokeObjectURL(url);
    downloadBlobFile(pdf, fileName);

    return;
  }

  window.setTimeout(() => URL.revokeObjectURL(url), PDF_URL_REVOKE_DELAY_MS);
};

export const useDownloadServiceCaseInvoiceDocument = (action: 'download' | 'print' | 'open') => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const getInvoiceFileNameKey = (type: ServiceCaseInvoiceType) =>
    match(type)
      .with('invoice', () => 'entity.invoice.labels.invoiceFile' as const)
      .with('invoice-internal', () => 'entity.invoice.labels.invoiceInternalFile' as const)
      .with('invoice-proforma', () => 'entity.invoice.labels.invoiceProformaFile' as const)
      .with(
        'tax-document-for-payment',
        () => 'entity.invoice.labels.taxDocumentForPaymentFile' as const
      )
      .with(
        'corrective-tax-document',
        () => 'entity.invoice.labels.correctiveTaxDocumentFile' as const
      )
      .with('balance-invoice', () => 'entity.invoice.labels.invoiceBalanceFile' as const)
      .with(
        'internal-corrective-tax-document',
        () => 'entity.invoice.labels.internalCorrectiveTaxDocumentFile' as const
      )
      .exhaustive();

  const downloadInvoice = (
    invoiceId: string,
    invoiceNumber: string | Nullish,
    type: ServiceCaseInvoiceType = 'invoice'
  ) => {
    setIsLoading(true);
    setIsError(false);

    exportServiceCaseInvoicePdf(invoiceId, type)
      .then((pdf) => {
        const fileName = i18n.t(getInvoiceFileNameKey(type), {invoiceNumber});

        match(action)
          .with('print', () => printPDF(pdf))
          .with('open', () => openPdfInNewTab(pdf, fileName))
          .with('download', () => downloadBlobFile(pdf, fileName))
          .exhaustive();
      })
      .catch((e) => {
        console.error(e);
        setIsError(true);
        captureException(e);
        showNotification.error();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return [downloadInvoice, {isLoading, isError}] as const;
};
